import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ContentContext.Provider value={{ items, loaded, reload: load }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}

/**
 * Hook returning the admin-editable text for a key, in current language.
 * Falls back to i18n key (and then to raw key) until content is loaded.
 */
export function useText(contentKey, i18nFallbackKey) {
  const ctx = useContext(ContentContext);
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2);
  if (ctx && ctx.items && ctx.items.length) {
    const found = ctx.items.find((x) => x.key === contentKey);
    if (found) return lang === "en" ? found.en : found.fr;
  }
  return i18nFallbackKey ? t(i18nFallbackKey) : "";
}

/**
 * Hook returning the admin-editable image URL for a key.
 * Falls back to the provided default URL until content loads.
 */
export function useImage(contentKey, defaultUrl) {
  const ctx = useContext(ContentContext);
  if (ctx && ctx.items && ctx.items.length) {
    const found = ctx.items.find((x) => x.key === contentKey);
    if (found && found.url) return found.url;
  }
  return defaultUrl;
}
